import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
} from "@mui/material";
import { Delete, Save, Add, Warning, ArrowBack, Refresh } from "@mui/icons-material";
import biosenseAPI from "../../../../api/biosense";
import ecoisAPI from "../../../../api/ecois";
import useConfirmDialog from "../../../../utils/useConfirmDialogue";
import useFeedbackDialog from "../../../../utils/useFeedbackDialogue";
import dayjs from "dayjs";
import { formatAlertMessage } from "../../../../utils/formatAlertMessage";
import { useTranslation } from "react-i18next";

const AlertSettings = ({ device }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [monitoringDataTypes, setMonitoringDataTypes] = useState([]);
  const [alertSettings, setAlertSettings] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [error, setError] = useState(null);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  // 新增：操作历史记录栈
  const [operationHistory, setOperationHistory] = useState([]);

  const confirmAction = useConfirmDialog();
  const { showSuccess, showError } = useFeedbackDialog();

  // 加载设备数据
  useEffect(() => {
    fetchDeviceData();
  }, []);

  // 成功提示
  useEffect(() => {
    if (requestSuccess) {
      const timer = setTimeout(() => {
        setRequestSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [requestSuccess]);

  // 获取设备数据
  const fetchDeviceData = async () => {
    try {
      setLoading(true);

      if (device.seriesAbbr === "esp32s3") {
        const deviceDescRaw = await biosenseAPI.getDeviceDescription({ sn: device.sn });
        const res = Object.keys(deviceDescRaw.parameters[0]);
        setMonitoringDataTypes(res);
      } else {
        const deviceModeRaw = await biosenseAPI.getDeviceMode({ sn: device.sn });
        const deviceDescRaw = await ecoisAPI.getDeviceDescription({ sn: device.sn });
        console.log(deviceDescRaw);
        let deviceDesc;
        const parameters = deviceDescRaw.parameters[0];
        const deviceMode = deviceModeRaw.mode;
        if (deviceMode === null) {
          deviceDesc = deviceDescRaw;
        } else {
          if (deviceMode === "soilwater" && parameters.waterLevel) {
            delete parameters.waterLevel;
          } else if (deviceMode === "waterlevel" && parameters.soilwater) {
            delete parameters.soilwater;
          }
          deviceDesc = deviceDescRaw;
        }
        const dataTypeMapping = {
          lightIntensity: "light",
          airTemperature: "airtemp",
          relativeHumidity: "airhumid",
          waterLevel: "waterlevel",
          soilwater: "soilhumid",
          pm10: "pm10",
          "pm2.5": "pm2_5",
          outsideVoltage: "volt",
          battery: "battery",
          soiltemp: "soiltemp",
          temp: "temp",
          tds: "tds",
        };
        const res = Object.keys(parameters).map((key) => dataTypeMapping[key]);

        setMonitoringDataTypes(res);
      }

      const alertsResponse = await biosenseAPI.getAlertSettings({ sn: device.sn });
      setAlertSettings(
        alertsResponse.map((alert, index) => ({
          id: index + 1,
          dataType: alert.dataType,
          comparison: alert.comparison,
          threshold: alert.threshold.toString(),
        })),
      );

      // 获取警报历史数据
      const alertHistoryResponse = await biosenseAPI.getAlertHistory({ sn: device.sn });
      const formattedAlertHistory = alertHistoryResponse.alerts.map((alert) => ({
        id: alert.id,
        time: dayjs(alert.recorded_at).format("YYYY-MM-DD HH:mm"),
        type: alert.type,
        message: alert.alert_text,
      }));
      setAlertHistory(formattedAlertHistory);
    } catch (error) {
      setError(error.message);
      showError("Load Failed", error.message);
    } finally {
      setLoading(false);
    }
  };

  // 重新加载设定
  const handleReloadSettings = () => {
    fetchDeviceData();
    // 清空操作历史记录
    setOperationHistory([]);
  };

  // 添加警报设置
  const handleAddAlarmSetting = () => {
    if (monitoringDataTypes.length === 0) {
      showError("No Data Types", "No monitoring data types available for this device");
      return;
    }

    const newSettings = [
      ...alertSettings,
      {
        id: Date.now(),
        dataType: monitoringDataTypes[0],
        comparison: ">",
        threshold: "",
      },
    ];
    // 记录操作到历史栈
    recordOperation({ type: "add", prevSettings: alertSettings, newSettings });
    setAlertSettings(newSettings);
  };

  // 更新警报设置
  const handleAlarmSettingChange = (index, field, value) => {
    const newAlarmSettings = [...alertSettings];
    const prevValue = newAlarmSettings[index][field];
    newAlarmSettings[index][field] = value;
    // 记录操作到历史栈
    recordOperation({ type: "edit", index, field, prevValue, newSettings: newAlarmSettings });
    setAlertSettings(newAlarmSettings);
  };

  // 删除警报设置
  const handleDeleteAlarmSetting = (index) => {
    confirmAction(
      {
        title: "Delete Alert Setting?",
        text: "This alert setting will be permanently deleted.",
        icon: "warning",
      },
      () => {
        const newAlarmSettings = [...alertSettings];
        const deletedSetting = newAlarmSettings.splice(index, 1)[0];
        // 记录操作到历史栈
        recordOperation({ type: "delete", index, deletedSetting, newSettings: newAlarmSettings });
        setAlertSettings(newAlarmSettings);
      },
    );
  };

  // 保存警报设置
  const handleSaveAlertSettings = async () => {
    try {
      const invalidSettings = alertSettings.some(
        (setting) => !setting.threshold || isNaN(Number(setting.threshold)),
      );

      if (invalidSettings) {
        showError("Validation Error", "Please enter valid threshold values for all alerts");
        return;
      }

      setButtonDisabled(true);
      await biosenseAPI.updateAlertSettings({
        sn: device.sn,
        alerts: alertSettings.map((setting) => ({
          dataType: setting.dataType,
          comparison: setting.comparison,
          threshold: Number(setting.threshold),
        })),
      });

      showSuccess("Settings Saved", "Alert settings have been updated");
      setRequestSuccess(true);
      setButtonDisabled(false);
      // 保存成功后清空操作历史记录
      setOperationHistory([]);
    } catch (error) {
      showError("Save Failed", error.message);
      setButtonDisabled(false);
    }
  };

  // 格式化数据类型显示
  const formatDataType = (dataType) => {
    const typeMap = {
      light: t("device.alertsettings_lightintensity"),
      airtemp: t("device.alertsettings_airtemperature"),
      airhumid: t("device.alertsettings_airhumidity"),
      waterlevel: t("device.alertsettings_waterlevel"),
      soilhumid: t("device.alertsettings_soilmoisture"),
      pm10: t("PM10"),
      pm2_5: t("PM2.5"),
      volt: t("device.alertsettings_voltage"),
      battery: t("device.alertsettings_battery"),
      soiltemp: t("soiltemperature"),
      temp: t("device.alertsettings_temp"),
      tds: t("device.alertsettings_tds"),
    };
    return typeMap[dataType] || dataType;
  };

  // 记录操作到历史栈
  const recordOperation = (operation) => {
    let newHistory = [...operationHistory, operation];
    if (newHistory.length > 20) {
      newHistory = newHistory.slice(1);
    }
    setOperationHistory(newHistory);
  };

  // 返回上一步
  const handleGoBack = () => {
    if (operationHistory.length === 0) {
      return;
    }
    const lastOperation = operationHistory.pop();
    setOperationHistory(operationHistory);
    switch (lastOperation.type) {
      case "add":
        setAlertSettings(lastOperation.prevSettings);
        break;
      case "edit":
        const editSettings = [...alertSettings];
        editSettings[lastOperation.index][lastOperation.field] = lastOperation.prevValue;
        setAlertSettings(editSettings);
        break;
      case "delete":
        const deleteSettings = [...alertSettings];
        deleteSettings.splice(lastOperation.index, 0, lastOperation.deletedSetting);
        setAlertSettings(deleteSettings);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <Card
        sx={{ boxShadow: 0, borderRadius: 12, border: "1px solid #e0e0e0", overflow: "hidden" }}
      >
        <CardContent sx={{ p: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <Warning sx={{ mr: 2, color: "#f57c00" }} />
            <Typography variant='h5' sx={{ color: "#333", fontWeight: 600 }}>
              {t("device.details_alertsettings")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ boxShadow: 0, borderRadius: 12, border: "1px solid #e0e0e0", overflow: "hidden" }}>
      <CardContent sx={{ p: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Warning sx={{ mr: 2, color: "#f57c00" }} />
          <Typography variant='h5' sx={{ color: "#333", fontWeight: 600 }}>
            {t("device.details_alertsettings")}
          </Typography>
          {/* <Tooltip title="How to use this feature">
            <IconButton size="small" color="primary">
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip> */}
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {requestSuccess && (
          <Alert severity='success' sx={{ mb: 4 }}>
            Alert settings saved successfully!
          </Alert>
        )}

        {/* 警报设置列表 */}
        <Box sx={{ mb: 6 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Tooltip title={t("back")}>
                <IconButton
                  size='small'
                  color='primary'
                  onClick={handleGoBack}
                  disabled={operationHistory.length === 0}
                >
                  <ArrowBack fontSize='small' />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("device.alertsettings_reloadsettings")}>
                <IconButton size='small' color='primary' onClick={handleReloadSettings}>
                  <Refresh fontSize='small' />
                </IconButton>
              </Tooltip>
            </Box>
            <Button
              variant='contained'
              color='primary'
              onClick={handleAddAlarmSetting}
              disabled={monitoringDataTypes.length === 0 || buttonDisabled}
              size='small'
            >
              <Add fontSize='small' sx={{ mr: 1 }} />
              {t("device.alertsettings_addalert")}
            </Button>
          </Box>

          {alertSettings.length === 0 ? (
            <Box sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: 8, textAlign: "center" }}>
              <Typography variant='body1' color='text.secondary'>
                {t("device.alertsettings_noalertsettings.")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden" }}>
              {alertSettings.map((setting, index) => (
                <Box
                  key={setting.id}
                  sx={{
                    p: 3,
                    borderBottom: index < alertSettings.length - 1 ? "1px solid #e0e0e0" : "none",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      transition: "background-color 0.2s",
                    },
                  }}
                >
                  <Grid container spacing={3} alignItems='center'>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>{t("device.alertsettings_datatype")}</InputLabel>
                        <Select
                          value={setting.dataType}
                          label={t("device.alertsettings_datatype")}
                          onChange={(e) =>
                            handleAlarmSettingChange(index, "dataType", e.target.value)
                          }
                          disabled={buttonDisabled}
                        >
                          {monitoringDataTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {formatDataType(type)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3.5}>
                      <FormControl fullWidth>
                        <InputLabel>{t("device.alertsettings_comparison")}</InputLabel>
                        <Select
                          value={setting.comparison}
                          label={t("device.alertsettings_comparison")}
                          onChange={(e) =>
                            handleAlarmSettingChange(index, "comparison", e.target.value)
                          }
                          disabled={buttonDisabled}
                        >
                          <MenuItem value='>'>{t("device.alertsettings_greaterthan")}</MenuItem>
                          <MenuItem value='<'>{t("device.alertsettings_lessthan")}</MenuItem>
                          <MenuItem value='='>{t("device.alertsettings_equalto")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2.5}>
                      <TextField
                        fullWidth
                        label={t("device.alertsettings_thresholdvalue")}
                        value={setting.threshold}
                        onChange={(e) =>
                          handleAlarmSettingChange(index, "threshold", e.target.value)
                        }
                        type='number'
                        disabled={buttonDisabled}
                      />
                      {setting.dataType === "soilhumid" && (
                        <Typography variant='caption' color='text.secondary'>
                          {"Example: input 2.1 means 21%"}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <IconButton
                        color='error'
                        onClick={() => handleDeleteAlarmSetting(index)}
                        disabled={buttonDisabled}
                        size='small'
                      >
                        <Delete fontSize='small' />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* 保存按钮 */}
        <Button
          fullWidth
          variant='contained'
          color='primary'
          onClick={handleSaveAlertSettings}
          disabled={alertSettings.length === 0 || buttonDisabled}
          sx={{ height: 56 }}
        >
          <Save fontSize='small' sx={{ mr: 1 }} />
          {t("device.alertsettings_savealertsettings")}
        </Button>

        {/* 警报历史 */}
        <Box sx={{ mt: 8 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 3 }}>
            {t("device.alertsettings_recentalerts")}
          </Typography>

          {alertHistory.length === 0 ? (
            <Box sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: 8, textAlign: "center" }}>
              <Typography variant='body1' color='text.secondary'>
                {t("device.alertsettings_norecentalertstodisplay.")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden" }}>
              {alertHistory.map((alert, index) => (
                <Box
                  key={`alert_${alert.id}${index}`}
                  sx={{
                    p: 3,
                    borderBottom: index < alertHistory.length - 1 ? "1px solid #e0e0e0" : "none",
                  }}
                >
                  <Grid container spacing={3} alignItems='center'>
                    <Grid item xs={12} sm={4}>
                      <Typography variant='body1' fontWeight='500'>
                        {alert.time}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Warning sx={{ color: "#f57c00", mr: 2, fontSize: 18 }} />
                        <Typography variant='body1'>
                          {formatAlertMessage(t, alert.message)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AlertSettings;
